import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MaterialDialog from './dialog';
import MaterialButton from './button';
import Colors from '../utils/theme/color';
import { useScreenSizeApplier } from '../hooks';

interface InactivityWarningModalProps {
  isOpen: boolean;
  remainingTime: number;
  onStayLoggedIn: () => void;
  onLogoutNow: () => void;
}

const InactivityWarningModal: React.FC<InactivityWarningModalProps> = ({
  isOpen,
  remainingTime,
  onStayLoggedIn,
  onLogoutNow,
}) => {
  const { t } = useTranslation();
  const { mergeStyles } = useScreenSizeApplier();

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!isOpen) return;
      
      // Enter or Space to stay logged in
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onStayLoggedIn();
      }
      // Escape to logout
      else if (event.key === 'Escape') {
        event.preventDefault();
        onLogoutNow();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
      // Focus on the dialog for accessibility
      setTimeout(() => {
        const dialog = document.querySelector('[data-testid="inactivity-warning-dialog"]');
        if (dialog) {
          (dialog as HTMLElement).focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, onStayLoggedIn, onLogoutNow]);

  return (
    <MaterialDialog
      open={isOpen}
      onClose={() => {}} // Prevent closing by clicking outside
      maxWidth="sm"
      sx={mergeStyles({
        styles: {
          padding: 4,
          textAlign: 'center',
        },
        tabletStyles: {
          padding: 2,
          margin: 2,
          maxHeight: '90vh',
          overflow: 'auto',
        },
      })}
    >
      <Box
        data-testid="inactivity-warning-dialog"
        tabIndex={-1}
        sx={mergeStyles({
          styles: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            padding: 3,
            outline: 'none',
          },
          tabletStyles: {
            padding: 2,
            gap: 2,
          },
        })}
      >
        {/* Warning Icon */}
        <Box
          sx={mergeStyles({
            styles: {
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: Colors.actionColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              marginBottom: 1,
            },
            tabletStyles: {
              width: 50,
              height: 50,
              fontSize: 20,
            },
          })}
          role="img"
          aria-label="Warning icon"
        >
          ⚠️
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          sx={mergeStyles({
            styles: {
              fontWeight: 600,
              color: Colors.titleColor,
              marginBottom: 1,
            },
            tabletStyles: {
              fontSize: '1.25rem',
              textAlign: 'center',
            },
          })}
          role="heading"
          aria-level={1}
        >
          Session Timeout Warning
        </Typography>

        {/* Main Message */}
        <Typography
          variant="body1"
          sx={mergeStyles({
            styles: {
              color: Colors.titleColor,
              fontSize: 16,
              lineHeight: 1.5,
              marginBottom: 2,
            },
            tabletStyles: {
              fontSize: 14,
              textAlign: 'center',
            },
          })}
          aria-live="polite"
        >
          You will be logged out in <strong>{formatTime(remainingTime)} mins</strong> due to inactivity.
          <br />
          Stay logged in?
        </Typography>

        {/* Countdown Display */}
        <Box
          sx={mergeStyles({
            styles: {
              padding: 2,
              backgroundColor: Colors.cyanColor,
              borderRadius: 2,
              border: `1px solid ${Colors.lightBlueColor}`,
              marginBottom: 2,
              position: 'relative',
              minWidth: 120,
            },
            tabletStyles: {
              padding: 1.5,
              minWidth: 100,
            },
          })}
          role="timer"
          aria-label={`Time remaining: ${formatTime(remainingTime)}`}
          aria-live="polite"
          aria-atomic="true"
        >
          <Typography
            variant="h4"
            sx={mergeStyles({
              styles: {
                fontWeight: 700,
                color: remainingTime <= 30 ? Colors.redColor : Colors.primaryColor,
                fontFamily: 'monospace',
                transition: 'color 0.3s ease',
              },
              tabletStyles: {
                fontSize: '1.75rem',
              },
            })}
          >
            {formatTime(remainingTime)}
          </Typography>
          
          {/* Progress indicator */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              height: 3,
              backgroundColor: remainingTime <= 30 ? Colors.redColor : Colors.primaryColor,
              borderRadius: '0 0 2px 2px',
              width: `${(remainingTime / 120) * 100}%`,
              transition: 'width 1s linear, background-color 0.3s ease',
            }}
          />
        </Box>

        {/* Action Buttons */}
        <Box
          sx={mergeStyles({
            styles: {
              display: 'flex',
              gap: 2,
              width: '100%',
              justifyContent: 'center',
            },
            tabletStyles: {
              flexDirection: 'column',
              gap: 1.5,
            },
          })}
          role="group"
          aria-label="Action buttons"
        >
          <MaterialButton
            id="logout-now-btn"
            title="No"
            variant="outlined"
            color="error"
            onClick={onLogoutNow}
            sx={mergeStyles({
              styles: {
                minWidth: 120,
                height: 48,
                fontSize: 16,
                fontWeight: 600,
              },
              tabletStyles: {
                minWidth: '100%',
                height: 56, // Larger touch target
                fontSize: 18,
              },
            })}
            aria-label="No, logout now"
          />
          <MaterialButton
            id="stay-logged-in-btn"
            title="Yes"
            variant="contained"
            color="primary"
            onClick={onStayLoggedIn}
            sx={mergeStyles({
              styles: {
                minWidth: 120,
                height: 48,
                fontSize: 16,
                fontWeight: 600,
                backgroundColor: Colors.primaryColor,
                color: Colors.white,
                '&:hover': {
                  backgroundColor: Colors.primaryColor,
                  opacity: 0.9,
                  color: Colors.white,
                },
              },
              tabletStyles: {
                minWidth: '100%',
                height: 56, // Larger touch target
                fontSize: 18,
              },
            })}
            aria-label="Yes, stay logged in"
          />
        </Box>

        {/* Additional Info */}
        <Typography
          variant="body2"
          sx={mergeStyles({
            styles: {
              color: Colors.greyColor,
              fontSize: 12,
              marginTop: 1,
            },
            tabletStyles: {
              fontSize: 11,
              textAlign: 'center',
            },
          })}
        >
          Click "Yes" to continue your session or "No" to logout immediately.
          <br />
          <em>Use Enter/Space for Yes, Escape for No</em>
        </Typography>
      </Box>
    </MaterialDialog>
  );
};

export default InactivityWarningModal;